import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AdditionalContext, AskAttachment, WebAppContext, AskReference, ActionButton, SeedingQuestion } from './copilotApi';

// Types
export interface Message {
    id: string;
    from: 'user' | 'bot';
    type: string;
    message: string;
    isSuggestion?: boolean;
    isInitialMessage?: boolean;
    isLoading?: boolean;
    isFeedbackUpvoted?: boolean | null;
    isFeedbackEnabled?: boolean;
    additionalUserFeedback?: string;
    isAnswerIrrelevant?: boolean;
    isAnswerInaccurate?: boolean;
    references?: AskReference[];
    actionButtons?: ActionButton[];
    intent?: string;
    question?: string;
    dateTime?: string;
}

export interface GroundingSource {
    id: string;
    type: 'file' | 'email' | 'calendar' | 'person' | 'action' | 'other';
    primaryText: string;
    secondaryText: string;
    avatar?: {
        image?: { src: string };
        icon?: React.ReactElement;
        name?: string;
        shape?: 'circular' | 'square';
    };
    metadata?: Record<string, any>;
}

// Grounding item for Actions tab
export interface GroundingAction {
    id: string;
    primaryText: string;
    secondaryText?: string;
    raidId?: number;
    tenantId?: string;
}

// Grounding item for People tab
export interface GroundingPerson {
    id: string;
    primaryText: string;
    secondaryText?: string;
    email?: string;
    avatarSrc?: string;
}

// Grounding item for Views tab
export interface GroundingView {
    id: string;
    name: string;
    scopeType: string;
    tenantId: string;
    docType: string;
    owner?: string;
    path?: string;
}

export interface Conversation {
    id: string;
    title: string;
    messages: Message[];
    createdAt: string;
    updatedAt: string;
}

export interface CopilotState {
    // Current conversation
    currentConversationId: string | null;
    conversations: Record<string, Conversation>;

    // Messages
    messages: Message[];
    inputValue: string;

    // Suggestions
    suggestions: SeedingQuestion[];

    // Grounding
    groundingMenuOpen: boolean;
    groundingSources: GroundingSource[];
    groundingActions: GroundingAction[];
    groundingPeople: GroundingPerson[];
    groundingViews: GroundingView[];
    selectedGroundingSources: string[];
    groundingSearchQuery: string;
    isLoadingGroundingSources: boolean;
    groundingError: string | null;

    // UI State
    isChatLoading: boolean;
    error: string | null;

    // Feedback modal
    feedbackModalOpen: boolean;
    feedbackMessageId: string | null;

    // Ask context parameters
    askContext: {
        additionalContext: AdditionalContext | null;
        attachments: AskAttachment[];
    };
}

export const initialState: CopilotState = {
    currentConversationId: null,
    conversations: {},
    messages: [],
    inputValue: '',
    suggestions: [],
    groundingMenuOpen: false,
    groundingSources: [],
    groundingActions: [],
    groundingPeople: [],
    groundingViews: [],
    selectedGroundingSources: [],
    groundingSearchQuery: '',
    isLoadingGroundingSources: false,
    groundingError: null,
    isChatLoading: false,
    error: null,
    feedbackModalOpen: false,
    feedbackMessageId: null,
    askContext: {
        additionalContext: null,
        attachments: [],
    },
};

// Async Thunks
export const sendMessage = createAsyncThunk(
    'copilot/sendMessage',
    async (
        payload: {
            message: string;
            conversationId?: string;
            groundingSources?: string[];
        },
        { rejectWithValue }
    ) => {
        try {
            // TODO: Replace with actual API call
            const response = await fetch('/api/copilot/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Failed to send message');
            }

            const data = await response.json();
            return data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const searchGroundingSources = createAsyncThunk(
    'copilot/searchGroundingSources',
    async (query: string, { rejectWithValue }) => {
        try {
            // TODO: Replace with actual search API call
            const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                throw new Error('Failed to search grounding sources');
            }

            const data = await response.json();
            return data.results as GroundingSource[];
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const loadSuggestions = createAsyncThunk(
    'copilot/loadSuggestions',
    async (_, { rejectWithValue }) => {
        try {
            // TODO: Replace with actual API call
            const response = await fetch('/api/copilot/suggestions', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                throw new Error('Failed to load suggestions');
            }

            const data = await response.json();
            return data.suggestions as SeedingQuestion[];
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

export const submitFeedback = createAsyncThunk(
    'copilot/submitFeedback',
    async (
        payload: {
            messageId: string;
            isPositive: boolean;
            feedback?: string;
        },
        { rejectWithValue }
    ) => {
        try {
            // TODO: Replace with actual API call
            const response = await fetch('/api/copilot/feedback', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error('Failed to submit feedback');
            }

            return payload;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    }
);

// Slice
const copilotSlice = createSlice({
    name: 'copilot',
    initialState,
    reducers: {
        // Input management
        setInputValue: (state, action: PayloadAction<string>) => {
            state.inputValue = action.payload;
        },
        clearInputValue: (state) => {
            state.inputValue = '';
        },

        // Message management
        addMessage: (state, action: PayloadAction<Message>) => {
            state.messages.push(action.payload);

            // Update conversation if exists
            if (state.currentConversationId && state.conversations[state.currentConversationId]) {
                state.conversations[state.currentConversationId].messages.push(action.payload);
                state.conversations[state.currentConversationId].updatedAt = new Date().toISOString();
            }
        },
        updateMessage: (state, action: PayloadAction<{ id: string; updates: Partial<Message> }>) => {
            const messageIndex = state.messages.findIndex(m => m.id === action.payload.id);
            if (messageIndex !== -1) {
                state.messages[messageIndex] = { ...state.messages[messageIndex], ...action.payload.updates };
            }

            // Update in conversation
            if (state.currentConversationId && state.conversations[state.currentConversationId]) {
                const convMessageIndex = state.conversations[state.currentConversationId].messages.findIndex(
                    m => m.id === action.payload.id
                );
                if (convMessageIndex !== -1) {
                    state.conversations[state.currentConversationId].messages[convMessageIndex] = {
                        ...state.conversations[state.currentConversationId].messages[convMessageIndex],
                        ...action.payload.updates,
                    };
                }
            }
        },
        clearMessages: (state) => {
            state.messages = [];
            state.currentConversationId = null;
        },
        clearInitialMessages: (state) => {
            // Remove any messages marked as initial/welcome messages
            state.messages = state.messages.filter(m => !m.isInitialMessage);

            // Also clear from conversation if exists
            if (state.currentConversationId && state.conversations[state.currentConversationId]) {
                state.conversations[state.currentConversationId].messages =
                    state.conversations[state.currentConversationId].messages.filter(m => !m.isInitialMessage);
            }
        },

        // Grounding menu
        setGroundingMenuOpen: (state, action: PayloadAction<boolean>) => {
            state.groundingMenuOpen = action.payload;
        },
        toggleGroundingMenu: (state) => {
            state.groundingMenuOpen = !state.groundingMenuOpen;
        },
        setGroundingSearchQuery: (state, action: PayloadAction<string>) => {
            state.groundingSearchQuery = action.payload;
        },
        selectGroundingSource: (state, action: PayloadAction<string>) => {
            if (!state.selectedGroundingSources.includes(action.payload)) {
                state.selectedGroundingSources.push(action.payload);
            }
        },
        deselectGroundingSource: (state, action: PayloadAction<string>) => {
            state.selectedGroundingSources = state.selectedGroundingSources.filter(
                id => id !== action.payload
            );
        },
        clearSelectedGroundingSources: (state) => {
            state.selectedGroundingSources = [];
        },
        setGroundingActions: (state, action: PayloadAction<GroundingAction[]>) => {
            state.groundingActions = action.payload;
        },
        setGroundingPeople: (state, action: PayloadAction<GroundingPerson[]>) => {
            state.groundingPeople = action.payload;
        },
        setGroundingViews: (state, action: PayloadAction<GroundingView[]>) => {
            state.groundingViews = action.payload;
        },

        // Suggestions
        setSuggestions: (state, action: PayloadAction<SeedingQuestion[]>) => {
            state.suggestions = action.payload;
        },

        // Feedback modal
        openFeedbackModal: (state, action: PayloadAction<string>) => {
            state.feedbackModalOpen = true;
            state.feedbackMessageId = action.payload;
        },
        closeFeedbackModal: (state) => {
            state.feedbackModalOpen = false;
            state.feedbackMessageId = null;
        },

        // Conversation management
        createConversation: (state, action: PayloadAction<{ id: string; title: string }>) => {
            const now = new Date().toISOString();
            state.conversations[action.payload.id] = {
                id: action.payload.id,
                title: action.payload.title,
                messages: [],
                createdAt: now,
                updatedAt: now,
            };
            state.currentConversationId = action.payload.id;
        },
        setCurrentConversation: (state, action: PayloadAction<string>) => {
            if (state.conversations[action.payload]) {
                state.currentConversationId = action.payload;
                state.messages = state.conversations[action.payload].messages;
            }
        },
        setCurrentConversationId: (state, action: PayloadAction<string>) => {
            state.currentConversationId = action.payload;
        },

        // Error handling
        clearError: (state) => {
            state.error = null;
            state.groundingError = null;
        },

        // Ask context management
        setAdditionalContext: (state, action: PayloadAction<AdditionalContext | null>) => {
            state.askContext.additionalContext = action.payload;
        },
        setWebAppContext: (state, action: PayloadAction<Partial<WebAppContext> | null>) => {
            if (action.payload) {
                // Merge the incoming webAppContext with the existing one
                const existingWebAppContext = state.askContext.additionalContext?.webAppContext || {};
                state.askContext.additionalContext = {
                    ...state.askContext.additionalContext,
                    webAppContext: {
                        ...existingWebAppContext,
                        ...action.payload,
                    },
                };
            } else {
                if (state.askContext.additionalContext) {
                    state.askContext.additionalContext.webAppContext = undefined;
                }
            }
        },
        addAttachment: (state, action: PayloadAction<AskAttachment>) => {
            // Prevent duplicates based on type + key
            const exists = state.askContext.attachments.some(
                att => att.type === action.payload.type && att.key === action.payload.key
            );
            if (!exists) {
                state.askContext.attachments.push(action.payload);
            }
        },
        removeAttachment: (state, action: PayloadAction<{ type: string; key: string }>) => {
            state.askContext.attachments = state.askContext.attachments.filter(
                att => !(att.type === action.payload.type && att.key === action.payload.key)
            );
        },
        setAttachments: (state, action: PayloadAction<AskAttachment[]>) => {
            state.askContext.attachments = action.payload;
        },
        clearAttachments: (state) => {
            state.askContext.attachments = [];
        },
        clearAskContext: (state) => {
            state.askContext = {
                additionalContext: null,
                attachments: [],
            };
        },
    },
    extraReducers: (builder) => {
        // Send message
        builder
            .addCase(sendMessage.pending, (state) => {
                state.isChatLoading = true;
                state.error = null;
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                state.isChatLoading = false;
                // Add bot response
                if (action.payload.response) {
                    const botMessage: Message = {
                        id: action.payload.id || Date.now().toString(),
                        from: 'bot',
                        type: 'response',
                        message: action.payload.response,
                        isLoading: false,
                        dateTime: new Date().toISOString(),
                    };
                    state.messages.push(botMessage);

                    if (state.currentConversationId && state.conversations[state.currentConversationId]) {
                        state.conversations[state.currentConversationId].messages.push(botMessage);
                        state.conversations[state.currentConversationId].updatedAt = new Date().toISOString();
                    }
                }
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.isChatLoading = false;
                state.error = action.payload as string;
            });

        // Search grounding sources
        builder
            .addCase(searchGroundingSources.pending, (state) => {
                state.isLoadingGroundingSources = true;
                state.groundingError = null;
            })
            .addCase(searchGroundingSources.fulfilled, (state, action) => {
                state.isLoadingGroundingSources = false;
                state.groundingSources = action.payload;
            })
            .addCase(searchGroundingSources.rejected, (state, action) => {
                state.isLoadingGroundingSources = false;
                state.groundingError = action.payload as string;
            });

        // Load suggestions
        builder
            .addCase(loadSuggestions.fulfilled, (state, action) => {
                state.suggestions = action.payload;
            });

        // Submit feedback
        builder
            .addCase(submitFeedback.fulfilled, (state, action) => {
                const messageIndex = state.messages.findIndex(m => m.id === action.payload.messageId);
                if (messageIndex !== -1) {
                    state.messages[messageIndex].isFeedbackUpvoted = action.payload.isPositive;
                    state.messages[messageIndex].additionalUserFeedback = action.payload.feedback;
                }
                state.feedbackModalOpen = false;
                state.feedbackMessageId = null;
            });
    },
});

export const {
    setInputValue,
    clearInputValue,
    addMessage,
    updateMessage,
    clearMessages,
    clearInitialMessages,
    setGroundingMenuOpen,
    toggleGroundingMenu,
    setGroundingSearchQuery,
    selectGroundingSource,
    deselectGroundingSource,
    clearSelectedGroundingSources,
    setGroundingActions,
    setGroundingPeople,
    setGroundingViews,
    setSuggestions,
    openFeedbackModal,
    closeFeedbackModal,
    createConversation,
    setCurrentConversation,
    setCurrentConversationId,
    clearError,
    // Ask context actions
    setAdditionalContext,
    setWebAppContext,
    addAttachment,
    removeAttachment,
    setAttachments,
    clearAttachments,
    clearAskContext,
} = copilotSlice.actions;

export default copilotSlice.reducer;

// Generated by GitHub Copilot | Updated on: 2026-04-01 | 4 lines modified | Changed suggestions state type from string[] to SeedingQuestion[] for tenant-aware filtering
